import { useCallback, useState, useRef, useEffect } from 'react';
import { useEventListener } from './useEventListener.js';
import { useIsServerEffect } from './useIsServerEffect.js';

const LOCAL_STORAGE_STATE_EVENT = 'local-storage-state';
function parseJSON(value) {
  try {
    return value === 'undefined' ? undefined : JSON.parse(value ?? '');
  } catch {
    return undefined;
  }
}
const useLocalStorageState = (key, initialValue) => {
  const isSSR = useIsServerEffect();
  const readValue = useCallback(() => {
    if (isSSR) {
      return initialValue;
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? parseJSON(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  }, [initialValue, key, isSSR]);
  const [storedValue, setStoredValue] = useState(readValue);
  const setValueRef = useRef();
  setValueRef.current = value => {
    try {
      const newValue = value instanceof Function ? value(storedValue) : value;
      window.localStorage.setItem(key, JSON.stringify(newValue));
      setStoredValue(newValue);
      window.dispatchEvent(new Event(LOCAL_STORAGE_STATE_EVENT));
    } catch (error) {
      console.error(error);
    }
  };
  const setValue = useCallback(value => setValueRef.current?.(value), []);
  useEffect(() => {
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSSR]);
  const handleStorageChange = useCallback(() => {
    setStoredValue(readValue());
  }, [readValue]);
  useEventListener('storage', handleStorageChange);
  useEventListener(LOCAL_STORAGE_STATE_EVENT, handleStorageChange);
  return [storedValue, setValue];
};

export { useLocalStorageState };
